import apiClient from './client';

// ─── Typen ──────────────────────────────────────────────────────────────────

export type ConditionCategory = 'disease' | 'hereditary' | 'cause_of_death';

export interface CatalogEntry {
  id: string;
  label: string;
  synonyms: string[];
  category: ConditionCategory;
  icd10: string | null;
  icd11: string | null;
  omim: string | null;
  orphaCode: string | null;
  hpoIds: string[];
  geneSymbol: string | null;
  isHereditary: boolean;
  inheritance: string | null;
  penetrance: number | null;
  description: string | null;
  isSystem: boolean;
}

export interface ConditionSource {
  id: string;
  sourceId: string;
  personConditionId: string | null;
  personTraitId: string | null;
  notes: string | null;
}

export interface PersonCondition {
  id: string;
  personId: string;
  catalogId: string | null;
  freeText: string | null;
  diagnosedDateText: string | null;
  diagnosedDate: string | null;
  ageAtOnset: number | null;
  status: string | null;
  ascertainment: 'not_assessed' | 'assessed_negative' | 'assessed_positive';
  certainty: 'documented' | 'reported' | 'suspected';
  isCauseOfDeath: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  catalog: CatalogEntry | null;
  sources: ConditionSource[];
}

export interface PersonTrait {
  id: string;
  personId: string;
  traitType: string;
  valueCode: string | null;
  valueNumber: number | null;
  valueText: string | null;
  asOfDateText: string | null;
  certainty: 'confirmed' | 'reported' | 'inferred' | 'family_lore';
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  sources: ConditionSource[];
}

export interface MedicalConsent {
  id: string;
  personId: string;
  scope: 'record' | 'share_relatives';
  grantedBy: string | null;
  grantedAt: string;
  revokedAt: string | null;
  note: string | null;
}

// ─── DTOs ─────────────────────────────────────────────────────────────────

export interface CreateConditionDto {
  catalog_id?: string | null;
  free_text?: string | null;
  diagnosed_date_text?: string | null;
  age_at_onset?: number | null;
  status?: string | null;
  ascertainment?: string;
  certainty?: string;
  is_cause_of_death?: boolean;
  notes?: string | null;
}
export type UpdateConditionDto = CreateConditionDto;

export interface CreateTraitDto {
  trait_type: string;
  value_code?: string | null;
  value_number?: number | null;
  value_text?: string | null;
  as_of_date_text?: string | null;
  certainty?: string;
  notes?: string | null;
}
export type UpdateTraitDto = Omit<CreateTraitDto, 'trait_type'>;

// ─── Katalog ─────────────────────────────────────────────────────────────

export async function searchCatalog(params?: {
  q?: string;
  category?: ConditionCategory;
  hereditary?: boolean;
}): Promise<CatalogEntry[]> {
  const { data } = await apiClient.get<{ data: CatalogEntry[] }>('/medical/catalog', {
    params: {
      ...(params?.q ? { q: params.q } : {}),
      ...(params?.category ? { category: params.category } : {}),
      ...(params?.hereditary !== undefined ? { hereditary: String(params.hereditary) } : {}),
    },
  });
  return data.data;
}

/** Eingabeformat für Katalog-Einträge (snake_case wie alle medical-Request-Bodies). */
export interface CatalogEntryInput {
  label: string;
  synonyms?: string[];
  category: ConditionCategory;
  icd10?: string | null;
  icd11?: string | null;
  omim?: string | null;
  orpha_code?: string | null;
  hpo_ids?: string[];
  gene_symbol?: string | null;
  is_hereditary?: boolean;
  inheritance?: string | null;
  penetrance?: number | null;
  description?: string | null;
}

export async function createCatalogEntry(input: CatalogEntryInput): Promise<CatalogEntry> {
  const { data } = await apiClient.post<CatalogEntry>('/medical/catalog', input);
  return data;
}

export async function updateCatalogEntry(id: string, input: Partial<CatalogEntryInput>): Promise<CatalogEntry> {
  const { data } = await apiClient.patch<CatalogEntry>(`/medical/catalog/${id}`, input);
  return data;
}

export async function deleteCatalogEntry(id: string): Promise<void> {
  await apiClient.delete(`/medical/catalog/${id}`);
}

// ─── Diagnosen / Erkrankungen ────────────────────────────────────────────

export interface ConditionsResponse {
  data: PersonCondition[];
  hereditaryHidden: number;
}

export async function getConditions(personId: string): Promise<ConditionsResponse> {
  const { data } = await apiClient.get<ConditionsResponse>(`/medical/persons/${personId}/conditions`);
  return data;
}

export async function createCondition(personId: string, dto: CreateConditionDto): Promise<PersonCondition> {
  const { data } = await apiClient.post<PersonCondition>(`/medical/persons/${personId}/conditions`, dto);
  return data;
}

export async function updateCondition(id: string, dto: UpdateConditionDto): Promise<PersonCondition> {
  const { data } = await apiClient.patch<PersonCondition>(`/medical/conditions/${id}`, dto);
  return data;
}

export async function deleteCondition(id: string): Promise<void> {
  await apiClient.delete(`/medical/conditions/${id}`);
}

// ─── Phänotypische Merkmale ─────────────────────────────────────────────────

export async function getTraits(personId: string): Promise<PersonTrait[]> {
  const { data } = await apiClient.get<{ data: PersonTrait[] }>(`/medical/persons/${personId}/traits`);
  return data.data;
}

export async function createTrait(personId: string, dto: CreateTraitDto): Promise<PersonTrait> {
  const { data } = await apiClient.post<PersonTrait>(`/medical/persons/${personId}/traits`, dto);
  return data;
}

export async function updateTrait(id: string, dto: UpdateTraitDto): Promise<PersonTrait> {
  const { data } = await apiClient.patch<PersonTrait>(`/medical/traits/${id}`, dto);
  return data;
}

export async function deleteTrait(id: string): Promise<void> {
  await apiClient.delete(`/medical/traits/${id}`);
}

// ─── Einwilligung (Consent) ──────────────────────────────────────────────

export async function getConsents(personId: string): Promise<MedicalConsent[]> {
  const { data } = await apiClient.get<{ data: MedicalConsent[] }>(`/medical/persons/${personId}/consents`);
  return data.data;
}

export async function grantConsent(
  personId: string,
  dto: { scope: MedicalConsent['scope']; note?: string | null },
): Promise<MedicalConsent> {
  const { data } = await apiClient.post<MedicalConsent>(`/medical/persons/${personId}/consents`, dto);
  return data;
}

export async function revokeConsent(id: string): Promise<MedicalConsent> {
  const { data } = await apiClient.delete<MedicalConsent>(`/medical/consents/${id}`);
  return data;
}

// ─── Vererbung (Vorfahren-Ansicht) ───────────────────────────────────────

export interface InheritancePersonLite {
  id: string;
  firstName: string;
  lastName: string | null;
  gender: string | null;
  birthDateText: string | null;
  deathDateText: string | null;
  isLiving: boolean;
}

export interface AncestorEntry extends InheritancePersonLite {
  /** 1 = Eltern, 2 = Großeltern, 3 = Urgroßeltern, … */
  generation: number;
  /** z. B. "Vater", "Großmutter (väterlicherseits)", "Urgroßvater (mütterlicherseits)" */
  relationLabel: string;
  /** Linie über den Vater/die Mutter der Ausgangsperson; null wenn nicht bestimmbar */
  side: 'paternal' | 'maternal' | null;
}

export interface InheritanceData {
  root: InheritancePersonLite;
  ancestors: AncestorEntry[];
}

/** Vorfahren-Gerüst (ohne Gesundheitsdaten – die laden die bestehenden
 *  Conditions/Traits-Endpoints pro Person, inkl. Consent-/Sensitiv-Filter). */
export async function getInheritance(personId: string, generations = 4): Promise<InheritanceData> {
  const { data } = await apiClient.get<InheritanceData>(`/medical/inheritance/${personId}`, {
    params: { generations },
  });
  return data;
}
